import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {combineLatest, Observable, ReplaySubject} from "rxjs";

import {PartnerFacility} from "../../../../shared/interface/partner-facility";
import {
  CityDobryAgent,
  FacilityDobryAgent,
  GooglePlaceDobryAgent
} from "../../../../shared/interface/facility-dobry-agent";
import {Ofwca} from "../../../../shared/interface/ofwca";
import {first, take, takeUntil} from "rxjs/operators";
import {Router} from "@angular/router";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {FormHelperService} from "../../../../shared/services/form-helper.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {DobryAgentFacilityService} from "../../../../shared/services/dobry-agent-facility.service";
import {AngularEditorConfig} from "@kolkov/angular-editor";
import {Partner} from "../../../../shared/interface/partner";
import {Dictionary} from "../../../../shared/interface/dictionary";
import {PARTNER_TAGS_GOOD_AGENT} from "../../../../shared/services/dict.service";
import {ImageDragAndDropItem} from "../../../../shared/components/image-drag-and-drop/image-drag-and-drop.component";
import {environment} from "../../../../../environments/environment";
import {HttpErrorResponse} from "@angular/common/http";

@Component({
  selector: 'app-dobry-agent-facility-add',
  templateUrl: './dobry-agent-facility-add.component.html',
  styleUrls: ['./dobry-agent-facility-add.component.scss']
})
export class DobryAgentFacilityAddComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() facility: Observable<PartnerFacility>;
  @Input() facilityDobryAgent: Observable<FacilityDobryAgent | null>;
  @Input() ofwcas: Observable<Ofwca[]>;

  @Output() closeModification = new EventEmitter<any>();

  partnerFacility?: PartnerFacility = null;
  facilityDobryAgentObject?: FacilityDobryAgent = null;

  processingRequest = false;
  facilityDobryAgentForm: FormGroup;
  dobryAgentBaseUrl = environment.dobryAgentUrl;

  ofwcaList: Ofwca[] = [];

  readonly openingHoursDays = [
    {key: 'poniedzialek', label: 'Poniedziałek'},
    {key: 'wtorek', label: 'Wtorek'},
    {key: 'sroda', label: 'Środa'},
    {key: 'czwartek', label: 'Czwartek'},
    {key: 'piatek', label: 'Piątek'},
    {key: 'sobota', label: 'Sobota'},
    {key: 'niedziela', label: 'Niedziela'},
  ];

  dayOffDateControl = new FormControl(null);
  dayOffDateFromControl = new FormControl(null);
  dayOffDateToControl = new FormControl(null);

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '220px',
    minHeight: '160px',
    maxHeight: '400px',
    width: '100%',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Wpisz opis placówki...',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    sanitize: false,
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript',
        'justifyRight',
        'justifyFull',
        'indent',
        'outdent',
        'insertUnorderedList',
        'insertOrderedList',
        'heading',
        'fontName',
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'link',
        'unlink',
        'insertImage',
        'insertVideo',
      ],
      [
        // 'toggleEditorMode'
      ]
    ]
  };

  constructor(
    private router: Router,
    private facilityDobryAgentService: DobryAgentFacilityService,
    private fb: FormBuilder,
    private formHelper: FormHelperService,
    private snackBar: MatSnackBar,
  ) {
    this.facilityDobryAgentForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      type: [''],
      serviceType: [''],
      phoneNumber: ['', [
        Validators.required,
        Validators.pattern(/^(?:\+?48)?(?:\s?\d{3}){3}$/),
      ]],
      email: ['', [
        Validators.required,
        Validators.email,
      ]],
      status: [''],

      active: [false],

      address: this.fb.group({
        street: this.fb.control('', [Validators.required]),
        houseNumber: this.fb.control('', [Validators.required]),
        apartmentNumber: this.fb.control(''),
        uuid: this.fb.control('', [Validators.required]),
        city: this.fb.control('', [Validators.required]),
        zipCode: this.fb.control('', [Validators.required,
          Validators.minLength(6),
          Validators.pattern('^[0-9]{2,5}(:|\\-)?[0-9]{3,4}')]),
      }),

      openHours: this.fb.group({
        poniedzialek: this.generateOpeningHoursDayGroup(),
        wtorek: this.generateOpeningHoursDayGroup(),
        sroda: this.generateOpeningHoursDayGroup(),
        czwartek: this.generateOpeningHoursDayGroup(),
        piatek: this.generateOpeningHoursDayGroup(),
        sobota: this.generateOpeningHoursDayGroup(),
        niedziela: this.generateOpeningHoursDayGroup(),
      }),
      daysOffFromWork: this.fb.array([]),
      googlePlaceId: [''],
      googleBusinessUrl: [''],

      seoSlug: [''],
      seoTitle: [''],
      seoDescription: [''],
      seoKeywords: [''],

      facebook: [''],
      website: [''],

      affiliateCode: [''],

      parking: [false],
      accessibilityFeatures: [false],
      bestAgents: [false],

      subCategories: [[]],
      insuranceCompanies: [[]],
      paymentMethods: [[]],

      chronieInsuranceProducts: [[]],
      images: [[]],
    });
  }


  private generateOpeningHoursDayGroup(): FormGroup {
    return this.fb.group({
      openTime: [''],
      closeTime: [''],
      closed: [false],
    });
  }

  ngOnInit(): void {
    this.facilityDobryAgentService.getCityDobryAgentSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((items) => {
      });
    combineLatest([
      this.facility,
      this.facilityDobryAgent,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([partnerFacility, facilityDobryAgent]) => {
        this.partnerFacility = partnerFacility;
        this.facilityDobryAgentObject = facilityDobryAgent;

        if (facilityDobryAgent) {
          this.patchForm(facilityDobryAgent);
        }

        // Przy tworzeniu nowej wizytówki DA kopiujemy adres z placówki ASF24.
        // Istniejącej wizytówki nie nadpisujemy danymi z ASF24.
        if (!facilityDobryAgent?.uuid) {
          this.patchAddressFromPartnerFacility(partnerFacility);
        }
      });

    this.ofwcas
      .pipe(takeUntil(this.destroyed$))
      .subscribe((ofwcas: Ofwca[]) => {
        this.ofwcaList = ofwcas ?? [];
      });
  }

  private patchForm(facility: FacilityDobryAgent): void {
    if (!facility) {
      return;
    }
    this.facilityDobryAgentForm.patchValue({
      name: facility.name ?? '',
      description: facility.description ?? '',
      type: facility.type?.id ?? facility.type ?? '',
      serviceType: facility.serviceType?.id ?? facility.serviceType ?? '',
      phoneNumber: facility.phoneNumber ?? '',
      email: facility.email ?? '',
      status: facility.status?.id ?? facility.status ?? '',

      active: facility.active,

      googlePlaceId: facility.googlePlaceId ?? '',
      googleBusinessUrl: facility.googleBusinessUrl ?? '',

      seoSlug: facility.seoSlug ?? '',
      seoTitle: facility.seoTitle ?? '',
      seoDescription: facility.seoDescription ?? '',
      seoKeywords: facility.seoKeywords ?? '',

      facebook: facility.facebook ?? '',
      website: facility.website ?? '',

      affiliateCode: facility.affiliateCode ?? '',

      parking: !!facility.parking,
      accessibilityFeatures: !!facility.accessibilityFeatures,
      bestAgents: !!facility.bestAgents,

      subCategories: facility.subCategories?.map((item: any) => item.uuid ?? item.id) ?? [],
      insuranceCompanies: facility.insuranceCompanies?.map((item: any) => item.uuid ?? item.id) ?? [],
      paymentMethods: facility.paymentMethods?.map((item: any) => item.uuid ?? item.id) ?? [],
      images: this.mapFacilityImages(facility),
      chronieInsuranceProducts: facility.chronieInsuranceProducts?.map((item: any) => item.uuid ?? item.id) ?? [],
    });


    if (facility.address) {
      this.facilityDobryAgentForm.get('address')?.patchValue({
        street: facility.address.street ?? '',
        houseNumber: facility.address.houseNumber ?? '',
        apartmentNumber: facility.address.apartmentNumber ?? '',

        // UUID adresu
        uuid: facility.address.uuid ?? '',

        // UUID miasta z DobryAgent
        city: '',

        zipCode: facility.address.zipCode ?? '',
      }, { emitEvent: false });
    }
    this.patchCityByName(facility.address?.city);
    this.patchOpeningHours(facility.openHours);
    this.patchDaysOffFromWork(facility.daysOffFromWork);
  }

  private patchAddressFromPartnerFacility(facility: PartnerFacility): void {
    const address = facility?.address;

    if (!address) {
      return;
    }

    this.facilityDobryAgentForm.get('address')?.patchValue({
      street: address.street ?? '',
      houseNumber: address.houseNumber ?? '',
      apartmentNumber: address.apartmentNumber ?? '',
      uuid: address.uuid ?? '',
      city: '',
      zipCode: address.zipCode ?? '',
    }, { emitEvent: false });

    // ASF24 przechowuje nazwę miasta, a formularz DA oczekuje UUID miasta
    // z bazy DobryAgent, dlatego rozwiązujemy UUID po nazwie.
    this.patchCityByName(address.city);
  }

  private patchOpeningHours(openHours: any): void {
    if (!openHours) {
      return;
    }

    this.openingHoursDays.forEach((day) => {
      const item = openHours[day.key];

      const openTime = this.normalizeTime(item?.openTime);
      const closeTime = this.normalizeTime(item?.closeTime);

      const closed =
        item?.openTime?.toLowerCase?.() === 'nieczynne'
        || item?.closeTime?.toLowerCase?.() === 'nieczynne'
        || (!openTime && !closeTime);

      const group = this.getOpeningHoursDayGroup(day.key);

      group.patchValue({
        openTime: closed ? '' : openTime,
        closeTime: closed ? '' : closeTime,
        closed,
      }, {
        emitEvent: false,
      });

      if (closed) {
        group.get('openTime')?.disable({ emitEvent: false });
        group.get('closeTime')?.disable({ emitEvent: false });
      } else {
        group.get('openTime')?.enable({ emitEvent: false });
        group.get('closeTime')?.enable({ emitEvent: false });
      }
    });
  }

  private normalizeTime(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    if (value.toLowerCase() === 'nieczynne') {
      return '';
    }

    const parts = value.split(':');

    if (parts.length < 2) {
      return '';
    }

    const hours = parts[0].padStart(2, '0');
    const minutes = parts[1].padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  private normalizeUrl(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return '';
    }

    if (/^https?:\/\//i.test(trimmedValue)) {
      return trimmedValue;
    }

    return `https://${trimmedValue}`;
  }

  getOpeningHoursDayGroup(dayKey: string): FormGroup {
    return this.facilityDobryAgentForm.get(['openHours', dayKey]) as FormGroup;
  }

  onClosedChange(dayKey: string): void {
    const group = this.getOpeningHoursDayGroup(dayKey);

    const openTimeControl = group.get('openTime');
    const closeTimeControl = group.get('closeTime');

    if (group.get('closed')?.value) {
      openTimeControl?.setValue('');
      closeTimeControl?.setValue('');

      openTimeControl?.disable({ emitEvent: false });
      closeTimeControl?.disable({ emitEvent: false });
    } else {
      openTimeControl?.enable({ emitEvent: false });
      closeTimeControl?.enable({ emitEvent: false });
    }
  }


  copyOpeningHoursToAllDays(sourceDayKey: string): void {
    const sourceGroup = this.getOpeningHoursDayGroup(sourceDayKey);
    const sourceValue = sourceGroup.getRawValue();
    const closed = !!sourceValue.closed;

    if (!closed && (!sourceValue.openTime || !sourceValue.closeTime)) {
      this.snackBar.open(
        'Uzupełnij godzinę otwarcia i zamknięcia przed kopiowaniem',
        '',
        { duration: 2500 }
      );

      return;
    }

    this.openingHoursDays.forEach((day) => {
      const group = this.getOpeningHoursDayGroup(day.key);
      const openTimeControl = group.get('openTime');
      const closeTimeControl = group.get('closeTime');

      group.patchValue({
        openTime: closed ? '' : sourceValue.openTime,
        closeTime: closed ? '' : sourceValue.closeTime,
        closed,
      }, { emitEvent: false });

      if (closed) {
        openTimeControl?.disable({ emitEvent: false });
        closeTimeControl?.disable({ emitEvent: false });
      } else {
        openTimeControl?.enable({ emitEvent: false });
        closeTimeControl?.enable({ emitEvent: false });
      }
    });

    const sourceDay = this.openingHoursDays.find((day) => day.key === sourceDayKey);

    this.snackBar.open(
      `Skopiowano godziny z dnia ${sourceDay?.label ?? ''} do wszystkich dni`,
      '',
      { duration: 2000 }
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  cancel(): void {
    if (!this.partnerFacility) {
      this.router.navigate(['/placowki']);
    } else {
      this.closeModification.emit();
    }
  }

  private prepareFacilityPayload(
    rawValue: any,
    asf24AddressUuid: string | null
  ): any {
    const address = rawValue.address ?? {};
    const name = String(rawValue.name ?? '').trim();

    return {
      // POST /api/internal/facility i PUT /api/internal/facility/{asf24AddressUuid}
      // korzystają z tego samego CreateFacilityForm, dlatego oba requesty
      // wysyłają ten sam płaski kontrakt.
      asf24AddressUuid,

      name,
      description: String(rawValue.description ?? '').trim() || 'Brak',

      type: rawValue.type,
      serviceType: rawValue.serviceType,

      email: String(rawValue.email ?? '').trim(),
      phoneNumber: this.normalizeFacilityPhoneNumber(rawValue.phoneNumber),

      street: address.street ?? '',
      buildingNumber: address.houseNumber ?? '',
      apartmentNumber: address.apartmentNumber ?? '',
      city: address.city,
      zipCode: address.zipCode ?? '',

      active: !!rawValue.active,

      website: this.normalizeUrl(rawValue.website),
      facebook: this.normalizeUrl(rawValue.facebook),

      parking: !!rawValue.parking,
      accessibilityFeatures: !!rawValue.accessibilityFeatures,
      bestAgents: !!rawValue.bestAgents,

      openHours: this.prepareOpeningHoursPayload(rawValue.openHours),

      daysOffFromWork: (rawValue.daysOffFromWork ?? []).filter(Boolean).sort(),
      paymentMethods: rawValue.paymentMethods ?? [],
      subCategories: rawValue.subCategories ?? [],
      insuranceCompanies: rawValue.insuranceCompanies ?? [],

      images: rawValue.images ?? [],

      seoSlug: String(rawValue.seoSlug ?? '').trim(),
      seoTitle: String(rawValue.seoTitle ?? '').trim() || name,
      seoDescription: rawValue.seoDescription ?? '',
      seoKeywords: rawValue.seoKeywords ?? '',

      deleted: false,

      googlePlaceId: rawValue.googlePlaceId ?? '',
      affiliateCode: rawValue.affiliateCode ?? '',
    };
  }

  private normalizeFacilityPhoneNumber(value: unknown): string {
    const digits = String(value ?? '')
      .replace(/\D/g, '');

    if (digits.length === 11 && digits.startsWith('48')) {
      return digits.substring(2);
    }

    return digits;
  }

  save(): void {
    this.clearBackendErrors(this.facilityDobryAgentForm);

    if (this.facilityDobryAgentForm.invalid) {
      this.facilityDobryAgentForm.markAllAsTouched();

      this.snackBar.open('Uzupełnij wymagane pola formularza', '', {
        duration: 2000,
      });

      return;
    }

    this.processingRequest = true;

    const rawValue = this.facilityDobryAgentForm.getRawValue();

    const asf24AddressUuid = this.partnerFacility?.address?.uuid
      ?? this.facilityDobryAgentObject?.asf24AddressUuid
      ?? rawValue.address?.uuid
      ?? null;

    const isEdit = !!this.facilityDobryAgentObject?.uuid;

    const payload = this.prepareFacilityPayload(rawValue, asf24AddressUuid);


    this.facilityDobryAgentService.sendForm(payload, isEdit ? asf24AddressUuid : null)
      .pipe(first())
      .subscribe({
        next: () => {
          this.processingRequest = false;

          this.snackBar.open(
            isEdit
              ? 'Pomyślnie zaktualizowano wizytówkę DA'
              : 'Pomyślnie utworzono wizytówkę DA',
            '',
            {
              duration: 2000,
            }
          );

          this.closeModification.emit();
        },
        error: (response: HttpErrorResponse) => {
          this.processingRequest = false;

          const message = this.applyDobryAgentBackendErrors(response);

          this.snackBar.open(message, '', {
            duration: 5000,
          });
        },
      });
  }

  private applyDobryAgentBackendErrors(response: HttpErrorResponse): string {
    const error = response?.error?.error ?? response?.error ?? {};
    const list = error?.list ?? {};

    Object.entries(list).forEach(([field, messages]) => {
      const control = this.resolveBackendControl(field);

      if (!control) {
        return;
      }

      const message = Array.isArray(messages)
        ? messages.filter(Boolean).join('. ')
        : String(messages ?? '');

      if (!message) {
        return;
      }

      control.setErrors({
        ...(control.errors ?? {}),
        backendError: message,
      });
      control.markAsTouched();

      control.valueChanges
        .pipe(
          take(1),
          takeUntil(this.destroyed$)
        )
        .subscribe(() => {
          this.clearBackendError(control);
        });
    });

    this.facilityDobryAgentForm.markAllAsTouched();

    return String(
      error?.text
      ?? response?.error?.message
      ?? 'Podczas zapisu wizytówki wystąpił błąd'
    ).trim();
  }

  private resolveBackendControl(field: string): AbstractControl | null {
    const fieldMap: Record<string, string> = {
      street: 'address.street',
      buildingNumber: 'address.houseNumber',
      houseNumber: 'address.houseNumber',
      apartmentNumber: 'address.apartmentNumber',
      city: 'address.city',
      zipCode: 'address.zipCode',
      addressUuid: 'address.uuid',
    };

    return this.facilityDobryAgentForm.get(fieldMap[field] ?? field);
  }

  private clearBackendErrors(control: AbstractControl): void {
    this.clearBackendError(control);

    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.values(control.controls).forEach((childControl: AbstractControl) => {
        this.clearBackendErrors(childControl);
      });
    }
  }

  private clearBackendError(control: AbstractControl): void {
    if (!control.errors?.backendError) {
      return;
    }

    const {backendError, ...otherErrors} = control.errors;

    control.setErrors(
      Object.keys(otherErrors).length
        ? otherErrors
        : null
    );
  }

  private prepareOpeningHoursPayload(openHours: any): any {
    const payload = {};

    this.openingHoursDays.forEach((day) => {
      const item = openHours[day.key];

      payload[day.key] = {
        openTime: item.closed ? 'nieczynne' : item.openTime,
        closeTime: item.closed ? 'nieczynne' : item.closeTime,
      };
    });

    return payload;
  }

  onCitySelected(city: CityDobryAgent | null): void {
    const addressGroup = this.facilityDobryAgentForm.get('address') as FormGroup;

    if (!city) {
      addressGroup.patchValue({
        district: '',
        commune: '',
        state: '',
      }, { emitEvent: false });

      return;
    }

    addressGroup.patchValue({
      district: city.district,
      commune: city.community,
      state: city.voivodeship,
    }, { emitEvent: false });
  }

  private patchCityByName(cityName?: string | null): void {
    if (!cityName) {
      return;
    }

    this.facilityDobryAgentService.searchCityDobryAgent(cityName, 20, 1)
      .pipe(take(1))
      .subscribe((response) => {
        const cities = response.items ?? [];

        const city = cities.find((item) => {
          return item.name?.toLowerCase() === cityName.toLowerCase();
        }) ?? cities[0] ?? null;

        if (!city) {
          return;
        }

        this.facilityDobryAgentForm.get('address.city')?.setValue(city.uuid, {
          emitEvent: false,
        });

        this.onCitySelected(city);
      });
  }
  onGooglePlaceSelected(place: GooglePlaceDobryAgent | null): void {
    this.facilityDobryAgentForm.patchValue({
      googleBusinessUrl: place
        ? this.buildGoogleBusinessUrl(place.place_id)
        : '',
    }, { emitEvent: false });
  }

  private buildGoogleBusinessUrl(placeId: string): string {
    return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeId)}`;
  }

  get daysOffFromWorkArray(): FormArray {
    return this.facilityDobryAgentForm.get('daysOffFromWork') as FormArray;
  }


  addSingleDayOff(): void {
    const date = this.normalizeDatepickerValue(this.dayOffDateControl.value);

    if (!date) {
      this.snackBar.open('Wybierz datę nieobecności', '', {
        duration: 2000,
      });

      return;
    }

    this.addDayOffDate(this.formatDateForPayload(date));

    this.dayOffDateControl.setValue(null);
  }

  addDayOffRange(): void {
    const from = this.normalizeDatepickerValue(this.dayOffDateFromControl.value);
    const to = this.normalizeDatepickerValue(this.dayOffDateToControl.value);

    if (!from || !to) {
      this.snackBar.open('Wybierz datę od i datę do', '', {
        duration: 2000,
      });

      return;
    }

    if (from.getTime() > to.getTime()) {
      this.snackBar.open('Data od nie może być późniejsza niż data do', '', {
        duration: 2500,
      });

      return;
    }

    const current = new Date(from);

    while (current.getTime() <= to.getTime()) {
      this.addDayOffDate(this.formatDateForPayload(current));
      current.setDate(current.getDate() + 1);
    }

    this.dayOffDateFromControl.setValue(null);
    this.dayOffDateToControl.setValue(null);
  }
  removeDayOff(index: number): void {
    this.daysOffFromWorkArray.removeAt(index);
  }
  private mapFacilityImages(facility: any): ImageDragAndDropItem[] {
    if (Array.isArray(facility.images) && facility.images.length > 0) {
      return facility.images
        .slice()
        .sort((a: any, b: any) => (a.position ?? 999) - (b.position ?? 999))
        .map((image: any, index: number) => ({
          location: image.location ?? '',
          data: image.data ?? undefined,
          size: image.size ?? 0,
          position: index + 1,
        }));
    }

    const images: ImageDragAndDropItem[] = [];

    if (facility.featuredImage?.src) {
      images.push({
        location: facility.featuredImage.location ?? facility.featuredImage.src,
        size: facility.featuredImage.size ?? 0,
        position: 1,
      });
    }

    if (Array.isArray(facility.galleryImage)) {
      facility.galleryImage.forEach((image: any) => {
        if (!image?.src) {
          return;
        }

        images.push({
          location: image.location ?? image.src,
          size: image.size ?? 0,
          position: images.length + 1,
        });
      });
    }

    return images;
  }
  private normalizeDatepickerValue(value: any): Date | null {
    if (!value) {
      return null;
    }

    // Moment.js
    if (typeof value?.toDate === 'function') {
      return this.clearDateTime(value.toDate());
    }

    // Date
    if (value instanceof Date) {
      return this.clearDateTime(value);
    }

    // string: YYYY-MM-DD
    if (typeof value === 'string') {
      const [year, month, day] = value.split('-').map(Number);

      if (!year || !month || !day) {
        return null;
      }

      return new Date(year, month - 1, day);
    }

    return null;
  }

  private addDayOffDate(date: string): void {
    const exists = this.daysOffFromWorkArray.controls.some((control) => {
      return control.value === date;
    });

    if (exists) {
      return;
    }

    this.daysOffFromWorkArray.push(this.fb.control(date));

    this.sortDaysOffFromWork();
  }

  private sortDaysOffFromWork(): void {
    const sortedDates = this.daysOffFromWorkArray.value
      .filter((date: string) => !!date)
      .sort();

    this.daysOffFromWorkArray.clear();

    sortedDates.forEach((date: string) => {
      this.daysOffFromWorkArray.push(this.fb.control(date));
    });
  }

  private clearDateTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private formatDateForPayload(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
  private patchDaysOffFromWork(daysOffFromWork: string[] | null | undefined): void {
    this.daysOffFromWorkArray.clear();

    (daysOffFromWork ?? [])
      .filter((date: string) => !!date)
      .sort()
      .forEach((date: string) => {
        this.daysOffFromWorkArray.push(this.fb.control(date));
      });
  }
}
